import React, { useState, useEffect } from 'react';
import { Lead } from '../types';

interface KanbanFiltersProps {
  leads: Lead[];
  onFilterChange: (filteredLeads: Lead[]) => void;
}

const KanbanFilters: React.FC<KanbanFiltersProps> = ({ leads, onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [consultorFilter, setConsultorFilter] = useState('');
  const [compradoFilter, setCompradoFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Obter consultores únicos
  const getUniqueConsultors = () => {
    const consultors = leads
      .filter(lead => lead.consultor)
      .map(lead => ({
        id: lead.consultor,
        name: lead.consultor_nome || `Consultor ${lead.consultor}`
      }));
    
    // Remover duplicatas
    const unique = consultors.filter((consultor, index, self) => 
      index === self.findIndex(c => c.id === consultor.id)
    );
    
    return unique;
  };

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...leads];

    // Filtro por texto (nome, CPF, telefone)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        (lead.reu_nome?.toLowerCase().includes(search)) ||
        (lead.autor_nome?.toLowerCase().includes(search)) ||
        (lead.reu_cpf?.includes(search)) ||
        (lead.autor_cpf?.includes(search)) ||
        (lead.reu_telefone_1?.includes(search)) ||
        (lead.numero_processo?.includes(search))
      );
    }

    // Filtro por consultor
    if (consultorFilter) {
      filtered = filtered.filter(lead => lead.consultor?.toString() === consultorFilter);
    }

    // Filtro por status de compra
    if (compradoFilter === 'sim') {
      filtered = filtered.filter(lead => lead.comprou === true);
    } else if (compradoFilter === 'nao') {
      filtered = filtered.filter(lead => lead.comprou === false || lead.comprou === null);
    }

    onFilterChange(filtered);
  }, [searchTerm, consultorFilter, compradoFilter, leads, onFilterChange]);

  const clearFilters = () => {
    setSearchTerm('');
    setConsultorFilter('');
    setCompradoFilter('');
  };

  const hasActiveFilters = searchTerm || consultorFilter || compradoFilter;

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          {/* Busca */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="🔍 Buscar por nome, CPF, telefone ou processo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Botão de Filtros Avançados */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              showFilters 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Filtros avançados"
          >
            🔧 Filtros
            {hasActiveFilters && (
              <span className="ml-1 bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                !
              </span>
            )}
          </button>
        </div>

        {/* Contador de resultados */}
        <div className="text-sm text-gray-600 ml-4">
          {leads.length} leads
        </div>
      </div>

      {/* Filtros Avançados */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtro por Consultor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consultor
              </label>
              <select
                value={consultorFilter}
                onChange={(e) => setConsultorFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os consultores</option>
                {getUniqueConsultors().map(consultor => (
                  <option key={consultor.id || ''} value={consultor.id || ''}>
                    {consultor.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Status de Compra */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status de Compra
              </label>
              <select
                value={compradoFilter}
                onChange={(e) => setCompradoFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="sim">💰 Compraram</option>
                <option value="nao">⏳ Não compraram</option>
              </select>
            </div>

            {/* Botões de Ação */}
            <div className="flex items-end space-x-2">
              <button
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                🗑️ Limpar
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm"
              >
                ✅ Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Indicadores de Filtros Ativos */}
      {hasActiveFilters && (
        <div className="mt-3 flex flex-wrap gap-2">
          {searchTerm && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
              Busca: "{searchTerm}"
              <button
                onClick={() => setSearchTerm('')}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                ✕
              </button>
            </span>
          )}
          {consultorFilter && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-green-100 text-green-800">
              Consultor: {getUniqueConsultors().find(c => c.id?.toString() === consultorFilter)?.name}
              <button
                onClick={() => setConsultorFilter('')}
                className="ml-1 text-green-600 hover:text-green-800"
              >
                ✕
              </button>
            </span>
          )}
          {compradoFilter && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
              {compradoFilter === 'sim' ? '💰 Compraram' : '⏳ Não compraram'}
              <button
                onClick={() => setCompradoFilter('')}
                className="ml-1 text-purple-600 hover:text-purple-800"
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default KanbanFilters;